'use client';

import { MutableRefObject, useEffect } from 'react';
import {
  DataGridPro,
  DataGridProProps,
  GridApiPro,
  gridClasses,
  useGridApiRef,
} from '@mui/x-data-grid-pro';
import { slate } from '@/lib/colours';
import CustomPagination, {
  PAGE_SIZE_OPTIONS,
} from './PublicSearchCustomPagination';
import useResponsive from '@/hooks/studbook/common/useResponsive';
import {
  NAV_HEIGHT,
  PUBLIC_SEARCH_HEADER_SECTION,
  PUBLIC_SEARCH_HEADER_SECTION_MOBILE,
} from '@/configs/layout';

export const searchApiRef = {
  current: null,
} as MutableRefObject<GridApiPro | null>;

export default function PublicSearchDataGrid(props: DataGridProProps) {
  const apiRef = useGridApiRef();
  const { isMobile } = useResponsive();

  useEffect(() => {
    if (!apiRef.current.showPreferences) return;
    searchApiRef.current = apiRef.current;
  }, [apiRef]);

  return (
    <DataGridPro
      {...props}
      apiRef={apiRef}
      autosizeOnMount={true}
      autosizeOptions={{
        includeHeaders: true,
        includeOutliers: true,
        expand: true,
      }}
      disableRowSelectionOnClick
      getRowClassName={(params) =>
        params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd'
      }
      paginationMode="server"
      pageSizeOptions={PAGE_SIZE_OPTIONS}
      slots={{
        ...props.slots,
        pagination: () => <CustomPagination rowCount={props.rowCount} />,
      }}
      sx={{
        height: `calc(100vh - ${
          isMobile
            ? PUBLIC_SEARCH_HEADER_SECTION_MOBILE
            : PUBLIC_SEARCH_HEADER_SECTION
        }px - ${NAV_HEIGHT * 2}px)`,
        border: 'none',
        paddingBlock: 0.5,

        [`& .${gridClasses.columnHeader}:focus-within, & .${gridClasses.cell}:focus-within`]:
          {
            outline: 'none',
          },
        '&.MuiDataGrid-root': {
          borderWidth: '0px',
          minHeight: '200px',
        },
        '& .MuiDataGrid-overlayWrapperInner': {
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        },
        '.MuiDataGrid-topContainer::after': {
          backgroundColor: 'unset',
        },
        '.MuiDataGrid-columnHeaderTitleContainerContent': {
          // Typography variant=titlesmall
          fontSize: '14px',
          fontWeight: '500',
          letterSpacing: '0.1px',
          color: `${slate[600]}`,
        },
        '.MuiDataGrid-columnHeader.pl': {
          paddingLeft: '26px',
        },
        '.MuiDataGrid-columnHeader--pinnedRight > .MuiDataGrid-columnSeparator':
          {
            display: 'none',
          },
        '.MuiDataGrid-columnHeader--withLeftBorder': {
          borderLeft: 'none',
        },
        '.MuiDataGrid-filler--pinnedRight': {
          borderLeft: 'none',
        },
        '.MuiDataGrid-row.even .MuiDataGrid-cell--pinnedRight': {
          backgroundColor: slate[50],
        },
        '.MuiDataGrid-row.odd .MuiDataGrid-cell--pinnedRight': {
          backgroundColor: 'white',
        },
        '.MuiDataGrid-cell': {
          borderWidth: '0px',
          fontSize: '14px',
          fontWeight: '400',
          letterSpacing: '0.25px',
          alignContent: 'center',
        },
        '.MuiDataGrid-footerContainer': {
          borderWidth: '0px',
        },
        [`& .${gridClasses.row}.even`]: {
          backgroundColor: slate[50],
        },
        [`& .${gridClasses.row}.odd`]: {},
        [`& .${gridClasses.row}:hover`]: {
          backgroundColor: slate[200],
        },
        ...props.sx,
      }}
    />
  );
}
